import { logEvent, utcUnixMs } from "@playbrain/config"

const rateLimitMap = new Map<string, { count: number; resetTime: number }>()
const RATE_LIMIT_WINDOW = 60 * 1000
const MAX_REQUESTS = 5
/*
 * One map now serves three namespaces -- the contact proxy plus newsletter
 * subscribe and unsubscribe -- so the cap has to cover all of them. At 1000 a
 * burst on one namespace evicted live records from another, silently resetting
 * a limit that was doing its job. Expired records are already dropped first
 * below, so the cap only bites under genuine concurrent load -- but note that
 * `count++` does not reinsert, so once it does bite the insertion-order
 * fallback evicts the BUSIEST keys first, which is backwards. Raising the cap
 * makes that far harder to reach; fixing the ordering is a separate change to
 * shared code that the contact form also depends on.
 */
const MAX_RATE_LIMIT_RECORDS = 5000

/**
 * Below API Gateway's ~29s ceiling and the site Lambda's 45s timeout, so a
 * stalled mailer surfaces as a logged failure here instead of a gateway timeout
 * with nothing written down. The mailer retries SES itself; retrying the whole
 * request here would risk sending the enquiry twice.
 */
const MAILER_REQUEST_TIMEOUT_MS = 20_000

type ContactProxyHandlerArgs = {
  request: Request
}

type ContactProxyDependencies = {
  mailerApiUrl?: string
  mailerProxySecret?: string
  fetchImpl?: typeof fetch
  siteOrigin?: string
  domainOrigin?: string
  timeoutMs?: number
}

function runtimeEnv(key: string): string | undefined {
  return (
    import.meta.env[key] ||
    (typeof process !== "undefined" ? process.env[key as keyof typeof process.env] : undefined)
  )
}

function allowedOriginSet(dependencies: ContactProxyDependencies = {}): Set<string> {
  const origins = [
    "https://playbrain.com",
    "https://www.playbrain.com",
    "https://dev.playbrain.com",
    "http://localhost:3000",
    "http://127.0.0.1:3000",
    dependencies.siteOrigin ?? runtimeEnv("SITE"),
    dependencies.domainOrigin ?? runtimeEnv("DOMAIN"),
  ].filter((origin): origin is string => typeof origin === "string" && origin.length > 0)
  return new Set(origins)
}

export function allowedCorsOrigin(
  origin: string | null,
  dependencies: ContactProxyDependencies = {}
): string | null {
  if (!origin) return null
  return allowedOriginSet(dependencies).has(origin) ? origin : null
}

function pruneRateLimitMap(now: number) {
  for (const [ip, record] of rateLimitMap) {
    if (now > record.resetTime) rateLimitMap.delete(ip)
  }

  while (rateLimitMap.size > MAX_RATE_LIMIT_RECORDS) {
    const oldestKey = rateLimitMap.keys().next().value
    if (!oldestKey) break
    rateLimitMap.delete(oldestKey)
  }
}

export function resetContactProxyRateLimitState() {
  rateLimitMap.clear()
}

export function enforceRateLimit(ip: string, now = utcUnixMs()): boolean {
  pruneRateLimitMap(now)
  const record = rateLimitMap.get(ip)

  if (!record || now > record.resetTime) {
    rateLimitMap.set(ip, { count: 1, resetTime: now + RATE_LIMIT_WINDOW })
    return true
  }

  if (record.count >= MAX_REQUESTS) return false
  record.count++
  return true
}

function corsHeaders(origin: string | null, dependencies: ContactProxyDependencies) {
  const headers = new Headers({
    "Access-Control-Allow-Methods": "POST, OPTIONS",
    "Access-Control-Allow-Headers": "Content-Type",
  })
  const allowedOrigin = allowedCorsOrigin(origin, dependencies)
  if (allowedOrigin) headers.set("Access-Control-Allow-Origin", allowedOrigin)
  return headers
}

export function createContactProxyHandlers(dependencies: ContactProxyDependencies = {}) {
  const fetchImpl = dependencies.fetchImpl ?? fetch

  return {
    POST: async ({ request }: ContactProxyHandlerArgs) => {
      const hasExplicitMailerEndpoint = Object.hasOwn(dependencies, "mailerApiUrl")
      const mailerApiUrl = hasExplicitMailerEndpoint
        ? dependencies.mailerApiUrl
        : runtimeEnv("MAILER_API_URL")
      if (!mailerApiUrl) {
        return Response.json({ error: "Mailer endpoint is not configured" }, { status: 500 })
      }

      const forwardedFor = request.headers.get("x-forwarded-for")
      const clientIp = forwardedFor?.split(",")[0]?.trim() || "unknown"

      const origin = request.headers.get("origin")
      const allowedOrigin = allowedCorsOrigin(origin, dependencies)
      if (origin && !allowedOrigin) {
        return Response.json({ success: false, message: "Origin not allowed" }, { status: 403 })
      }

      if (!enforceRateLimit(clientIp)) {
        return Response.json(
          { success: false, message: "Rate limit exceeded. Please try again later." },
          { status: 429 }
        )
      }

      const timeoutMs = dependencies.timeoutMs ?? MAILER_REQUEST_TIMEOUT_MS
      const abortController = new AbortController()
      const timeoutId = setTimeout(() => abortController.abort(), timeoutMs)

      try {
        const body = await request.text()
        const headers = new Headers({ "Content-Type": "application/json" })
        if (allowedOrigin) headers.set("Origin", allowedOrigin)
        const mailerProxySecret = dependencies.mailerProxySecret ?? runtimeEnv("MAILER_PROXY_SECRET")
        if (mailerProxySecret) headers.set("x-playbrain-mailer-token", mailerProxySecret)

        const response = await fetchImpl(mailerApiUrl, {
          method: "POST",
          headers,
          body,
          signal: abortController.signal,
        })
        const data = await response.text()

        if (!response.ok) {
          logEvent("error", "contact_proxy.mailer_error", {
            status: response.status,
            body: data.slice(0, 500),
          })
        }

        return new Response(data, {
          status: response.status,
          headers: new Headers({
            "Content-Type": response.headers.get("Content-Type") || "application/json",
            ...Object.fromEntries(corsHeaders(origin, dependencies)),
          }),
        })
      } catch (error) {
        const timedOut = error instanceof Error && error.name === "AbortError"
        logEvent("error", "contact_proxy.mailer_unreachable", {
          timedOut,
          timeoutMs,
          message: error instanceof Error ? error.message : "Unknown error",
        })
        return Response.json({ error: "Failed to send email" }, { status: timedOut ? 504 : 500 })
      } finally {
        clearTimeout(timeoutId)
      }
    },
    OPTIONS: async ({ request }: ContactProxyHandlerArgs) => {
      const origin = request.headers.get("origin")
      if (origin && !allowedCorsOrigin(origin, dependencies)) return new Response(null, { status: 403 })
      return new Response(null, {
        status: 204,
        headers: corsHeaders(origin, dependencies),
      })
    },
  }
}
